import type { ReactNode } from "react";
import { DragOverlay, type DropAnimation } from "@dnd-kit/core";
import { Tag } from "@design-system/Tag/Tag";
import type { BoardCardContext } from "./Board.types";
import { BOARD_CARD_SURFACE_ATTRIBUTE } from "./boardAttributes";
import type { BoardCellEntry } from "./boardCells";
import { BoardOverlayCard } from "./BoardOverlayCard";
import dragStyles from "./BoardDrag.module.scss";

// A deeper stack reads as clutter; the badge carries the exact count.
const STACK_DEPTH_LIMIT = 2;

interface BoardGroupOverlayProps<TItem> {
    /** The grabbed card during a pointer drag; nothing otherwise. */
    entry: BoardCellEntry<TItem> | undefined;
    /** Cards the drag carries, the grabbed one included. */
    count: number;
    isSelected: boolean;
    isRefused: boolean;
    dropAnimation: DropAnimation;
    renderCard: (item: TItem, context: BoardCardContext) => ReactNode;
}

// The overlay is a visual copy; its card must not be focusable. Its shadow
// and stack follow the card's body: around a drawer they would fill the
// corners beside it.
const prepareOverlay = (node: HTMLElement | null) => {
    if (node === null) {
        return;
    }
    node.inert = true;
    const surface = node.querySelector(`[${BOARD_CARD_SURFACE_ATTRIBUTE}]`);
    if (surface instanceof HTMLElement) {
        node.style.setProperty(
            "--board-overlay-surface-height",
            `${surface.offsetHeight}px`,
        );
    }
};

/** How many cards a drag carries, on the card that leads it. */
export const BoardCountBadge = ({ count }: { count: number }) => (
    <span aria-hidden className={dragStyles.countBadge}>
        <Tag size="md" color="accent" numeric className={dragStyles.countTag}>
            {count}
        </Tag>
    </span>
);

/**
 * The card under the pointer. Carrying a selection, it sits on a stack of
 * one or two offset layers with a badge counting every card that moves.
 */
export const BoardGroupOverlay = <TItem,>({
    entry,
    count,
    isSelected,
    isRefused,
    dropAnimation,
    renderCard,
}: Readonly<BoardGroupOverlayProps<TItem>>) => (
    <DragOverlay dropAnimation={dropAnimation}>
        {entry === undefined ? null : (
            <div
                ref={prepareOverlay}
                aria-hidden
                className={dragStyles.overlayCard}
                data-refused={isRefused}
            >
                {Array.from(
                    { length: Math.min(count - 1, STACK_DEPTH_LIMIT) },
                    (_, index) => (
                        <div
                            key={index}
                            className={dragStyles.stackLayer}
                            data-depth={index + 1}
                        />
                    ),
                )}
                <BoardOverlayCard
                    itemId={entry.itemId}
                    placementKey={entry.placementKey}
                >
                    {renderCard(entry.item, {
                        placement: entry.placement,
                        placementKey: entry.placementKey,
                        isSelected,
                    })}
                </BoardOverlayCard>
                {count > 1 && <BoardCountBadge count={count} />}
            </div>
        )}
    </DragOverlay>
);
